// The Get started walkthroughs. Each step points at a real element marked data-tour="<target>";
// `fallback` is shown when that element is not on screen (e.g. a state the user is not in).
// `marks` records a step that leaves no other trace once the tour is finished. `backStep` sends
// Back to the start of a form that has been submitted; `noBack` hides Back once it can't be undone.
// `undo` lists the elements Back clicks, in order, to put the previous step's screen back.
// `skipWhen` passes a step by, in the direction of travel, while that element (or any in a list) is on screen;
// a skipped step's `undo` only runs on the way back with `undoOnSkip`. `advanceWhen` also takes a list.
// `before` lists the elements Next clicks, in order, on the way into a step (e.g. closing a profile).
// `hint` replaces the "click the highlighted button" line on an action step.
import CareersPageExample from "../components/CareersPageExample";

// The four source boxes in the Track form, in the order they appear.
function SourcesExplainer() {
  const rows = [
    ["Official Careers Page", "The jobs list on the firm's own website. Included on every plan."],
    ["LinkedIn", "Roles the firm posts on LinkedIn."],
    ["eFinancialCareers", "Finance and banking roles posted there."],
    ["JobsDB", "Roles posted on JobsDB, popular across Asia."],
  ];
  return (
    <div>
      <p style={{ margin: "0 0 8px" }}>Tick one or more. We pull every open role from each and check again daily.</p>
      {rows.map(([name, text]) => (
        <div key={name} style={{ padding: "6px 0", borderTop: "1px solid var(--line)" }}>
          <b style={{ color: "var(--ink)", fontWeight: 600 }}>{name}</b>
          <div style={{ fontSize: 12.5 }}>{text}</div>
        </div>
      ))}
      <p style={{ margin: "8px 0 0", fontSize: 12.5 }}>LinkedIn, eFinancialCareers and JobsDB are part of the Growth plan.</p>
    </div>
  );
}

// Sample input the walkthroughs can type in for the user. The firm is already tracked with live
// roles, so following along shows jobs straight away instead of starting a new scrape.
const SAMPLE_FIRM = {
  name: "Baker McKenzie",
  url: "https://careers.bakermckenzie.com/en_US/opportunities/SearchJobs",
};
const EXAMPLE_PERSON =
  "Senior associate in banking & finance at a top-tier international law firm in Hong Kong, 5 to 8 years PQE, fluent Mandarin, experience with syndicated lending.";
const EXAMPLE_TITLE = "Banking & Finance Senior Associate";
const EXAMPLE_JD = `Our client, a top-tier international law firm, is hiring a Senior Associate for its Hong Kong banking & finance team.

What you'll do
- Lead syndicated and bilateral lending deals for banks and corporate borrowers
- Draft and negotiate facility agreements and security documents
- Supervise and train junior associates

What you'll bring
- 5 to 8 years PQE in banking & finance at a leading firm
- Admitted in Hong Kong, England & Wales or another common-law jurisdiction
- Fluent English and Mandarin`;
// The Find candidates tour branches on the mode picked: each branch's steps are passed by while the other's form is up.
const JD_SKIP = ["fc-describe", "fc-modes"];
const PERSON_SKIP = ["fc-jd", "fc-country"];
const EXAMPLE_PROMPT =
  "Write a short, warm first email to {{firstName}} about a {{role}} opportunity. Mention one thing from their background that fits, keep it under 120 words, and end by asking if they're open to a quick call this week. Don't name the client.";

export const TOURS = {
  // Plays by itself the first time a trial user opens a search's results (FindCandidatesPage); on Get started for every plan.
  // Started from Get started, the page opens the latest search that found people.
  results: {
    title: "Your results",
    route: "/find-candidates/history",
    marks: "results",
    steps: [
      {
        target: "fc-results-row",
        title: "Your results",
        body: "The best matches are at the top. Match runs from Very High down to Very Low.",
      },
      {
        target: "fc-results-criteria",
        title: "Must-haves and nice-to-haves",
        body: "Hover over must or nice to see which of your must-haves and nice-to-haves this person meets.",
        fallback: "Each person shows how many of your must-haves and nice-to-haves they meet. Hover over them to see which.",
      },
      {
        target: "fc-results-weaker",
        title: "Weaker matches",
        body: "People who fit less well are kept at the bottom of the list. Click here any time to show them.",
        skipWhen: "fc-no-weaker",
      },
      {
        target: "fc-results-row",
        title: "See their full profile",
        body: "Click a person to open their full profile.",
        advanceWhen: "fc-profile",
        hint: "Click the highlighted person to continue",
      },
      {
        target: "fc-profile",
        undo: ["fc-profile-close"],
        title: "Their full profile",
        body: "Their experience, education and skills, all in one place.",
        wide: true,
      },
      {
        target: "fc-profile-linkedin",
        title: "Open their LinkedIn",
        body: "Click the LinkedIn icon to see their LinkedIn profile in a new tab.",
        fallback: "When a person has a LinkedIn profile, the LinkedIn icon beside their name opens it in a new tab.",
      },
      {
        target: "fc-results-refine",
        before: ["fc-profile-close"],
        undo: ["fc-results-row"],
        title: "Not seeing the right people?",
        body: "Click here to find more people like the ones you like best, or to adjust the filters and search again.",
        locked: {
          when: "fc-similar-locked",
          body: "Click here to adjust the filters and search again. Finding more people like the ones you like best is part of the Growth plan.",
        },
        fallback: "Once a search has finished, Not seeing the right people? at the top lets you adjust the filters and search again.",
      },
    ],
  },
  jobs: {
    title: "Track a company",
    route: "/jobs",
    steps: [
      {
        target: "jobs-track",
        title: "Add a company",
        body: "Click Add jobs. We'll track a sample law firm together so you can see how it works.",
        advanceWhen: "jt-name",
      },
      {
        target: "jt-name",
        undo: ["jt-close"],
        title: "Start with the company's name",
        body: "Type the firm you want to follow. For this walkthrough we'll use Baker McKenzie.",
        fill: { value: SAMPLE_FIRM.name, label: "Use the sample" },
        requireFilled: true,
      },
      {
        target: "jt-sources",
        title: "Where to pull its jobs from",
        body: <SourcesExplainer />,
        wide: true,
      },
      {
        target: "jt-careers",
        title: "Tick Official Careers Page",
        body: "Let's use the firm's own website for this one.",
        advanceWhen: "jt-url",
      },
      {
        target: "jt-url",
        undo: ["jt-careers"],
        title: "Paste its careers page",
        body: <CareersPageExample />,
        wide: true,
        fill: { value: SAMPLE_FIRM.url, label: "Use the sample" },
        requireFilled: true,
      },
      {
        target: "jt-board-linkedin",
        title: "Add LinkedIn too",
        body: "Firms often post roles on LinkedIn that aren't on their own site. Tick LinkedIn. eFinancialCareers and JobsDB work the same way, so tick any others you want.",
        advanceWhen: "jt-find-boards",
        locked: {
          when: "jt-boards-locked",
          target: "jt-sources",
          body: "Firms often post roles on LinkedIn, eFinancialCareers and JobsDB that aren't on their own site. On Growth and above, tick any of them here and we pull those roles too.",
        },
      },
      {
        target: "jt-find-boards",
        undo: ["jt-board-linkedin"],
        title: "Find the firm on each board",
        body: "Click Find on job boards. We look the firm up on every board you ticked.",
        advanceWhen: "jt-cands-ready",
        locked: {
          when: "jt-boards-locked",
          target: "jt-boards-locked",
          body: "Once a board is ticked, Find on job boards looks the firm up on each one.",
        },
      },
      {
        target: "jt-cands",
        // Untick and tick again: a fresh tick drops the lookup, so Find on job boards is back.
        undo: ["jt-board-linkedin", "jt-board-linkedin"],
        title: "Check the match",
        body: "We pick the closest company on each board. If it's the wrong one, choose another from the list, or pick None of these to drop that board. View jobs shows what we'd pull.",
        wide: true,
        locked: {
          when: "jt-boards-locked",
          target: "jt-boards-locked",
          body: "We then pick the closest company on each board for you to check, and preview the jobs we'd pull. Upgrade to Growth from Plans any time.",
        },
      },
      {
        target: "jt-submit",
        title: "Track it",
        body: "Click Track. We'll pull every open role from the careers page and any boards you ticked, and check again every day.",
        advanceWhen: "jobs-tracklist",
      },
      {
        target: "jobs-tracklist",
        backStep: 0,
        title: "Your tracked firms",
        body: "A new firm shows Setting up while we read its page for the first time, usually a minute or two. Then it says Active · daily, with its number of open roles. If you see Couldn't read this site, check the link goes to the list of jobs.",
        wide: true,
      },
      {
        target: "jobs-tab-trackedjobs",
        title: "Your jobs show up here",
        body: "Every open role from the firms you track lands in Tracked jobs, and new ones arrive each day.",
      },
    ],
  },
  research: {
    title: "Research a company",
    route: "/research",
    marks: "research",
    steps: [
      {
        target: "research-search",
        title: "Look up a firm",
        body: "Search the firms you follow, or narrow them by country, industry or who's hiring now.",
      },
      {
        target: "research-first",
        title: "Open a company",
        body: "Click this firm to open its page. Any firm in the list works the same way.",
        advanceWhen: "research-head",
        fallback: "Firms show up here once you track some on the Jobs page. Try \u201cTrack a company\u201d first.",
      },
      {
        target: "research-head",
        backTo: "/research",
        title: "The firm at a glance",
        body: "Where it's based and what it does. Save bookmarks it, the paper plane writes to its HR team, and the trophy shows its Legal500 rankings when it has some.",
        wide: true,
      },
      {
        target: "research-stats",
        title: "The key numbers",
        body: "Open roles and in how many countries, new roles today, this week and this month, contacts on file, and news in the last 30 days.",
        wide: true,
      },
      {
        target: "research-trends",
        title: "Hiring trends",
        body: "How many roles it has posted over time. Switch between 30 days and 12 weeks, and see which countries it's hiring in next to it.",
      },
      {
        target: "research-contacts",
        title: "Who to talk to",
        body: "The people on file at the firm, ready to email from Outreach. Find contacts looks up its C-suite or HR team right here.",
        wide: true,
      },
      {
        target: "research-jobs",
        title: "Its latest roles",
        body: "The newest openings at this firm. View all opens every one of them on the Jobs page.",
      },
      {
        target: "research-signals",
        title: "Latest signals",
        body: "News, leadership moves and hiring changes we pick up about the firm, so you have a reason to get in touch.",
      },
    ],
  },
  people: {
    title: "Find candidates",
    route: "/find-candidates",
    steps: [
      {
        target: "fc-start",
        title: "Start a new search",
        body: "Click Start a new search.",
        advanceWhen: "fc-modes",
        fallback: "A search is already running. Once it finishes you can start a new one here.",
      },
      {
        target: "fc-mode-pick",
        undo: ["fc-back"],
        title: "Job description or a person?",
        body: "Have a job description? Click Job description and paste it. No JD to hand? Click Describe a person and say who you need.",
        advanceWhen: ["fc-jd", "fc-describe"],
      },
      // Job description branch: passed by when the user picked Describe a person (or went Back to the modes).
      {
        target: "fc-jd-title",
        undo: ["fc-back"],
        skipWhen: JD_SKIP,
        title: "Name the role",
        body: "The job title as you'd post it.",
        fill: { target: "fc-jd-title", value: EXAMPLE_TITLE, label: "Use an example" },
        requireFilled: true,
      },
      {
        target: "fc-jd-text",
        skipWhen: JD_SKIP,
        title: "Paste the job description",
        body: "Paste the full JD from anywhere, as it is. We pick out what to search for. Or let us fill in an example.",
        fill: { value: EXAMPLE_JD },
        requireFilled: true,
      },
      {
        target: "fc-hiring",
        skipWhen: JD_SKIP,
        title: "Who's hiring?",
        body: "Pick the client company, or tick Keep it confidential. Confidential is fine for a first try.",
      },
      {
        target: "fc-continue",
        skipWhen: JD_SKIP,
        title: "Continue",
        body: "Once there's a title, a description and a company (or it's confidential), click Continue.",
        advanceWhen: "fc-country",
      },
      // Describe a person branch: passed by on the JD form, and once the user is past it on Country.
      {
        target: "fc-describe",
        undo: ["fc-back"],
        skipWhen: PERSON_SKIP,
        title: "Who do you need?",
        body: "Write it the way you'd brief a colleague: role, seniority, where they've worked, must-haves. Or let us fill in an example.",
        fill: { value: EXAMPLE_PERSON },
        requireFilled: true,
      },
      {
        target: "fc-continue",
        skipWhen: PERSON_SKIP,
        title: "Continue",
        body: "Click Continue when you're happy with the description.",
        advanceWhen: "fc-hiring",
      },
      {
        target: "fc-title",
        undo: ["fc-back"],
        skipWhen: PERSON_SKIP,
        title: "Give the role a title",
        body: "We suggest a title from your description. Keep it, or type your own.",
        fill: { target: "fc-title", value: EXAMPLE_TITLE, label: "Use an example" },
        requireFilled: true,
      },
      {
        target: "fc-hiring",
        skipWhen: PERSON_SKIP,
        title: "Who's hiring?",
        body: "Pick the client company, or tick Keep it confidential. Confidential is fine for a first try.",
      },
      {
        target: "fc-continue",
        skipWhen: PERSON_SKIP,
        title: "Continue",
        body: "Once there's a title and a company (or it's confidential), click Continue.",
        advanceWhen: "fc-country",
      },
      {
        target: "fc-country",
        undo: ["fc-back"],
        title: "Where should we look?",
        body: "Add one or more countries, or choose Search worldwide.",
      },
      {
        target: "fc-continue",
        title: "Build the brief",
        body: "Click Continue. We'll read your description and turn it into a search brief.",
        advanceWhen: "fc-brief",
      },
      {
        target: "fc-brief",
        title: "Reading your description",
        body: "This takes a few seconds. We pick out the titles, keywords and past employers to search for.",
        waiting: "Building the brief…",
        advanceWhen: "fc-filters",
      },
      {
        target: "fc-filters",
        backStep: 12,
        undo: ["fc-brief-back"],
        title: "Check the brief",
        body: "These are the filters the search will use. Change anything that's off: add a title, remove an employer, tighten a requirement.",
        wide: true,
      },
      {
        target: "fc-launch",
        title: "Find candidates",
        body: "Happy with the brief? Click Find candidates.",
        advanceWhen: "fc-confirm-goal",
      },
      {
        target: "fc-confirm-goal",
        undo: ["fc-confirm-back"],
        // The free trial's goal is fixed, so there's nothing to choose: straight on to Start.
        skipWhen: "fc-goal-locked",
        undoOnSkip: true,
        title: "How many do you want?",
        body: "Choose how many strong candidates to aim for. The search uses credits as it goes, and stops when it reaches your number.",
      },
      {
        target: "fc-confirm-start",
        title: "Start the search",
        body: "Click Yes, start.",
        advanceWhen: "fc-finding",
        fallback: "The search didn't start. The message above the brief says why; fix that and click Find candidates again.",
      },
      {
        target: "fc-finding",
        noBack: true,
        title: "Your search is running",
        body: "It takes a few minutes, and it keeps going without you.",
      },
      {
        target: "fc-finding-back",
        noBack: true,
        title: "No need to wait",
        body: "Click Back. The search carries on while you grab a coffee, have a chat or look around the rest of the app.",
        advanceWhen: "fc-ongoing",
        fallback: "Your search has finished. The results are on screen.",
      },
      {
        target: "fc-ongoing",
        noBack: true,
        title: "Your search keeps going here",
        body: "It sits at the top of Your searches with how far it has got, plus Pause and Stop. When it's done, it stays in the list with the number of people found. Click it to see who we found. Meanwhile, carry on with Get started or explore the app.",
        fallback: "Your search has finished. Open it from Your searches to see who we found.",
      },
    ],
  },
  // Get started picks the short tours below when the full one would end on a click the account can't make.
  // Results, when none of the user's searches has found anyone yet (FindCandidatesPage swaps it in).
  resultsEmpty: {
    title: "Your results",
    route: "/find-candidates/history",
    steps: [
      {
        title: "Nothing to walk through yet",
        body: "None of your searches has found people yet. Once one does, come back to Get started and click Show me to go through its results.",
      },
    ],
  },
  // Find candidates or Track a company with the balance below zero, where both are refused.
  noCredits: {
    title: "Top up first",
    route: "/settings?tab=credits",
    steps: [
      {
        target: "credits-balance",
        title: "You're out of credits",
        body: "Not enough credits for this one: a search needs at least 60, and tracking a company needs 2,000 in your balance. Top up, then come back to Get started for the walkthrough.",
      },
      {
        target: "credits-buy",
        title: "Buy extra credits",
        body: "Credits come in blocks of 1,000 for $10, and they're added as soon as payment goes through.",
        fallback: "Ask your account owner to buy extra credits.",
      },
    ],
  },
  // Research before any firm is tracked: its list only holds firms the user follows.
  researchEmpty: {
    title: "Research a company",
    route: "/research",
    steps: [
      {
        target: "research-search",
        title: "Firms you follow show up here",
        body: "Research covers the firms you track on the Jobs page. Track one first with Track a company, then come back to see its profile, hiring trends, contacts and news.",
      },
    ],
  },
  // Find candidates while a one-at-a-time plan already has a search running (GetStartedPage picks it).
  peopleRunning: {
    title: "Find candidates",
    route: "/find-candidates",
    steps: [
      {
        target: "fc-ongoing",
        title: "Your search is still running",
        body: "It's at the top of Your searches. Click it to watch, pause or stop it, or leave it be: when it's done, it stays in the list with the people it found.",
        fallback: "Your search has just finished. Open it from Your searches to see who we found.",
      },
      {
        target: "fc-start",
        title: "One search at a time",
        body: "Your plan runs one search at a time, so this button unlocks once the current one finishes. Come back then and click Show again to walk through a new search.",
      },
    ],
  },
  saved: {
    title: "Save jobs and firms",
    route: "/jobs?tab=saved",
    steps: [
      {
        target: "jobs-tab-saved",
        title: "Your saved jobs",
        body: "Jobs you bookmark collect under this tab. Saved companies are a filter on the Research page.",
      },
      {
        target: "saved-list",
        title: "Bookmark from anywhere",
        body: "Use the bookmark icon on any job or firm. Candidates you save from a search go to Outreach instead, under that role's project.",
      },
    ],
  },
  outreach: {
    title: "Reach out",
    route: "/outreach",
    steps: [
      {
        target: "outreach-main",
        title: "Your projects and emails",
        body: "Every role you save candidates to becomes a project here. Open one, pick a candidate, draft the email (AI can help), and send it from your own mailbox.",
      },
    ],
  },
  signature: {
    title: "Add your signature",
    route: "/settings?tab=signatures",
    steps: [
      {
        target: "sig-new",
        title: "Add a signature",
        body: "Your signature goes at the bottom of the emails you send to candidates. Click the button to make one.",
        advanceWhen: "sig-name",
      },
      {
        target: "sig-name",
        undo: ["sig-cancel"],
        title: "Name it",
        body: "Just a label for you, like Default or Asia outreach, in case you keep more than one.",
        fill: { value: "Default", label: "Call it Default" },
        requireFilled: true,
      },
      {
        target: "sig-content",
        title: "Write or paste it",
        body: "Type your sign-off: name, title, phone, company. Or paste your signature from Outlook or Gmail; the formatting and logo come across.",
        wide: true,
      },
      {
        target: "sig-save",
        title: "Save it",
        body: "Click Create signature.",
        advanceOnClick: true,
      },
      {
        target: "sig-list",
        backStep: 0,
        title: "On every email",
        body: "The one marked Default is added when the AI writes an email in Outreach, and Insert signature in the composer adds it by hand. Set another as default any time, or click one to edit it.",
        wide: true,
      },
    ],
  },
  prompts: {
    title: "Write emails your way",
    route: "/settings?tab=prompts",
    steps: [
      {
        target: "prompt-new",
        title: "Save an AI prompt",
        body: "A prompt tells the AI how to write your outreach emails: the angle, the tone, what to mention. Click the button to make one.",
        advanceWhen: "prompt-name",
      },
      {
        target: "prompt-name",
        undo: ["prompt-cancel"],
        title: "Name it",
        body: "A short label so you can pick it later.",
        fill: { value: "Friendly first touch", label: "Use an example" },
        requireFilled: true,
      },
      {
        target: "prompt-body",
        title: "Tell the AI what you want",
        body: "Write it like a note to an assistant: what the email is for, what to mention and what to leave out. {{firstName}}, {{role}} and {{company}} are filled in for each person.",
        wide: true,
        fill: { value: EXAMPLE_PROMPT, label: "Use an example" },
        requireFilled: true,
      },
      {
        target: "prompt-save",
        title: "Save it",
        body: "Click Create prompt.",
        advanceOnClick: true,
      },
      {
        target: "prompt-list",
        backStep: 0,
        title: "Use it in Outreach",
        body: "When you write to a candidate, pick the prompt from the menu next to Personalize and the AI writes the email your way. Click the star to use one every time.",
        wide: true,
      },
    ],
  },
  credits: {
    title: "Track your spending",
    route: "/settings?tab=credits",
    marks: "credits",
    steps: [
      {
        target: "credits-balance",
        title: "Your credits live here",
        body: "This is your balance. Searches, previews, filter tuning and contact lookups take credits from it as they run. The credits count in the sidebar opens this page any time.",
      },
      {
        target: "credits-usd",
        title: "What a credit is worth",
        body: "1 credit is about $0.01, so $1 buys 100 credits and $10 buys 1,000. The figure under your balance is its rough dollar value.",
        fallback: "1 credit is about $0.01, so $1 buys 100 credits and $10 buys 1,000.",
      },
      {
        target: "credits-usage",
        title: "Where they went",
        body: "Every run and every top-up, newest first, with what it cost. Click a row with an arrow to see its parts. Look at what a typical week uses to see how long your balance will last.",
        wide: true,
      },
      {
        target: "credits-monthly",
        title: "Added every month",
        body: "Your plan adds this many credits each month. The Renews date below is when the next ones arrive.",
        fallback: "On a paid plan, your monthly credits and the date the next ones arrive show at the top of this page.",
      },
      {
        target: "credits-buy",
        title: "Running low?",
        body: "Buy extra credits any time, in blocks of 1,000 for $10. They're added as soon as payment goes through.",
        fallback: "Running low? Ask your account owner to buy extra credits.",
      },
    ],
  },
  alerts: {
    title: "Daily job alerts",
    route: "/settings?tab=email-list",
    marks: "alerts",
    steps: [
      {
        target: "alerts-switch",
        title: "New roles, every morning",
        body: "Turn this on to get one email a day with new openings at the firms you follow.",
        fallback: "Daily alerts are part of the Growth plan and up.",
      },
      {
        target: "alerts-test",
        title: "See what it looks like",
        body: "Send yourself a test now. It shows the last week of new roles.",
      },
    ],
  },
};
